import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApiResponse } from '@models/interfaces/core/ApiResponse.interface';
import { catchError, EMPTY, forkJoin, from, Observable, switchMap, tap } from 'rxjs';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { SiteImporterComponent } from '@components/admin/dialogs/site-importer/site-importer.component';
import { ConfigurationService } from './configuration.service';
import { DvmService } from './dvm.service';
import { SitePreviewComponent } from '@components/admin/dialogs/site-preview/site-preview.component';
import { ToastService } from './toast.service';
import { PreviewService } from './preview.service';
import { blobUrlToFile } from '@helpers/utils/blob-url-to-file.util';

export interface SiteImporterData {
  onSearch:          (query: string) => Observable<SearchResultResponse>;
  onStartConversion: (id: number, slug: string) => void;
}

export interface PreviewData {
  id: number; 
  isImportMode: boolean;
  importConfig: (id: number, open: boolean) => Observable<unknown>;
}

export type SearchResultResponse = ApiResponse<{
  title:        string;
  isCompatible: boolean;
  id:           number;
}[]> 

interface ConversorParams {
  inputJson: unknown;
  updateProgress: (progressData: ProgressData) => void;
}

interface ProgressData {
    progress: number;
    message: {
        title: string;
        text:  string;
    }
} 

type Conversor = (params: ConversorParams) => Promise<Configuration>;

interface ConversorModule {
  conversor: Conversor;
}

@Injectable({
  providedIn: 'root'
})
export class ConversorService {

  // SERVICES
  private readonly dialog:        Dialog                = inject(Dialog);
  private readonly http:          HttpClient            = inject(HttpClient);
  private readonly configuration: ConfigurationService  = inject(ConfigurationService);
  private readonly dvm:           DvmService            = inject(DvmService);
  private readonly toastr:        ToastService          = inject(ToastService);
  private readonly preview:       PreviewService        = inject(PreviewService);
  private readonly document:      Document              = inject(DOCUMENT);
  private readonly conversorBundlePath: string = 'assets/conversor.bundle.js';
  // ENDPOINTS
  private readonly endpoints: { search: string, import: string, config: string } = {
    search: environment.apiUrl + '/api/previews/conversor/search/',
    import: environment.apiUrl + '/api/previews/conversor/import/',
    config: environment.cdnBucket
  };

  // API METHODS
  private readonly searchEndpoint:    (query:string) => string = (query: string) => `${this.endpoints.search}${query}`;
  private readonly getConfigEndpoint: (slug:string)  => string = (slug: string)  => `${this.endpoints.config}${slug}/latest/master.json`;

  // DIALOGS
  private searchDialog:  DialogRef<unknown, SiteImporterComponent> | null = null;
  private previewDialog: DialogRef<unknown, SitePreviewComponent>  | null = null;

  // DATA
  private resultConfiguration: Configuration | null = null;
  private conversorModule: Promise<ConversorModule> | null = null;
  private readonly ignoreConversionProgress = (progressData: ProgressData): void => {
    void progressData;
  };

  // METHOD
  public openSearchDialog(onOpenImported: (slug: string) => void): void {

    this.searchDialog = this.dialog.open(SiteImporterComponent,{
      hasBackdrop: true,
      width: '600px',
      height: 'auto',
      data: {
        onSearch:          (query: string) => this.search(query),
        onStartConversion: (id: number, slug: string) => this.startConversion(id, slug, onOpenImported)
      }
    })

    // Limpiamos configuración de preview al cerrar diálogo de busqueda
    this.searchDialog?.closed.subscribe((): void => {
      this.resultConfiguration = null;
      this.configuration.resetConfiguration();
      this.configuration.togglePreviewMode(false);
    }); 

  }

  private search(query: string): Observable<SearchResultResponse> {

    return this.http.get<SearchResultResponse>(this.searchEndpoint(query)); 

  }

  private startConversion(id: number, slug: string, onOpenImported: (slug: string) => void): void {

    this.http.get(this.getConfigEndpoint(slug)).pipe(
      switchMap((data: unknown): Observable<Configuration> => {
        
        const params: ConversorParams = {
          inputJson:      data,
          updateProgress: this.ignoreConversionProgress
        };

        return from(
          this.loadConversor().then(({ conversor }: ConversorModule): Promise<Configuration> => conversor(params))
        );

      }),
      tap((result: Configuration): void => {
        
        // Añadimos Result
        this.resultConfiguration = result

        // Seteamos modo preview y cargamos configuración
        this.configuration.togglePreviewMode(true);

        // Reiniciamos viewer 
        // if(this.dvm.viewer && this.dvm.viewer.isInitialized()) this.dvm.resetViewer(false);

        // Cargamos configuración
        this.configuration.loadConfig(result);
        // this.configuration.setImportedPreview(result);
        
        // console.log(this.configuration)
        // Creamos diálogo de preview
        this.previewDialog = this.dialog.open(SitePreviewComponent, {
          hasBackdrop:  true,
          width:        '1100px',
          height:       '815px',
          disableClose: true,
          data: {
            id: id,
            isImportMode: true,
            importConfig: (id: number, open?: boolean): Observable<unknown> => this.importSite().pipe(
              switchMap((): Observable<unknown> => this.preview.getPreviews(1, undefined, true)),
              tap((): void => {
                this.dialog.closeAll();
                this.toastr.show('Site imported successfully!');
                this.http.patch(environment.apiUrl + `/api/sites/redirect/${id}`, {redirectUrl: environment.previewUrl + slug.toLowerCase()}).subscribe({
                  error: (): Promise<void> => this.toastr.show('Failed to set redirect URL. Please check the site configuration.')
                })
                if(open) onOpenImported(result.General.title);
              })
            )
          }
        })
        
      }),
      catchError((error): Observable<never> => {
        this.toastr.show('An error occurred during the conversion process. Please try again later.');
        console.error('Error during conversion:', error);
        return EMPTY;
    })
    ).subscribe();
   
  }

  private importSite():Observable<unknown> {
    
    if(!this.resultConfiguration) throw new Error('No configuration available for import');
    
    const formData: FormData        = new FormData(),
          conf:     Configuration   = this.resultConfiguration,
          blob:     Blob            = new Blob([JSON.stringify(conf)], { type: 'application/json' });

    formData.append('slug', conf.General.title);
    formData.append('url', environment.previewUrl + conf.General.title.toLowerCase());
    formData.append('active', "true")
    formData.append('test', "false")
    formData.append('mapId', conf.Dvm.viewer.mapId);
    formData.append('venueCode', conf.Dvm.viewer.venueId);
    formData.append('clientKey', "C-95a684ae-9a57-466e-9028-ce953194b7f5")
    formData.append('venueKey', 'V-XXXXX')
    formData.append('masterConfig', blob, 'masterConfig.json');

    return forkJoin({
      logo:     blobUrlToFile(conf.General.client.logo, 'logo.png'),
      favicon:  blobUrlToFile(conf.General.client.favicon, 'favicon.ico')
    }).pipe(switchMap(({logo, favicon}) => {
       
      formData.append('logo', logo, 'logo.png');
      formData.append('favicon', favicon, 'favicon.ico');

      return this.http.post(this.endpoints.import, formData);

      
    }))
  }

  private loadConversor(): Promise<ConversorModule> {
    const bundleUrl: string = new URL(this.conversorBundlePath, this.document.baseURI).href;
    this.conversorModule ??= import(/* @vite-ignore */ bundleUrl) as Promise<ConversorModule>;
    return this.conversorModule;
  }

}
